/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { styled } from "@mui/material/styles";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import { useNavigate } from "react-router-dom";
import ListItemText from "@mui/material/ListItemText";
import { fetchAllSubmissions } from "../../Services/redux/ApplicationDetailsSubmissions/ApplicationDetailsSubmissionsActions";
import "./ApplicationDetailsSideMenu.css";
import { getConfig } from "../../config";
import PropTypes from "prop-types";

const drawerWidth = 180;

const INFO_MENU_ITEMS = {
  default: ["Team Info", "Activity Log", "All Releases"],
  domain: ["Activity Log", "All Releases"],
  defaultWithManageClient: [
    "Team Info",
    "Activity Log",
    "All Releases",
    "Manage API Client",
  ],
  domainWithManageClient: [
    "Activity Log",
    "All Releases",
    "Manage API Client",
  ],
};

const SECTION_SCROLL_TARGETS = {
  "APK File": "apkFile",
  Description: "description",
  "Service Links": "service-links",
  Screenshots: "screenshots",
  "Additional Information": "additonalInformation",
  "Additional Documents": "additionalDocument",
  Category: "category",
  "Rollout Settings": "rollOutSettings",
  Licensing: "licensing",
  "Legal Documentation": "legal-documentation-section",
  "Cluster compatibility": "cluster-section",
  DLCM: "dlcm-section",
};

const hasAppData = (appData) =>
  Boolean(appData && Object.keys(appData).length > 0);

const getInitialSubmissionValue = (
  appMultiSubmissionId,
  submissionDetails,
  appData
) => {
  if (
    appMultiSubmissionId !== undefined &&
    appMultiSubmissionId !== "" &&
    typeof appMultiSubmissionId === "string" &&
    appMultiSubmissionId !== "true"
  ) {
    return appMultiSubmissionId;
  }

  if (submissionDetails?.id) {
    return submissionDetails.id;
  }

  if (hasAppData(appData) && appData.submissions && appData.submissions.length > 0) {
    return appData.submissions[0].id;
  }

  return "";
};

const getDefaultInfoData = (portal, idpLogin, domain) => {
  if (idpLogin) {
    if (portal === "domain-approver") {
      if (domain === "VWGroup") {
        return INFO_MENU_ITEMS.domain;
      }

      return INFO_MENU_ITEMS.domainWithManageClient;
    }

    return INFO_MENU_ITEMS.defaultWithManageClient;
  }

  if (portal === "domain-approver") {
    return INFO_MENU_ITEMS.domain;
  }

  return INFO_MENU_ITEMS.default;
};

const getManageClientInfoData = (portal, domain) => {
  if (portal === "domain-approver" || portal === "global-admin") {
    if (domain === "VWGroup") {
      return INFO_MENU_ITEMS.domain;
    }

    return INFO_MENU_ITEMS.domainWithManageClient;
  }

  return INFO_MENU_ITEMS.defaultWithManageClient;
};

const buildStandardSideMenu = ({
  idpLogin,
  isOemSeller,
  hasPaymentRole,
  submissionData,
}) => {
  if (idpLogin) {
    const sideMenu = [
      "APK File",
      "Description",
      "Screenshots",
      "Additional Information",
      "Additional Documents",
      "Cluster compatibility",
      "DLCM",
      "Licensing",
      "Category",
      "Legal Documentation",
      "Targeting",
      "Rollout Settings",
    ];

    if (hasPaymentRole) {
      sideMenu.push("Monetize");
    }

    return sideMenu;
  }

  if (isOemSeller) {
    const sideMenu = [
      "APK File",
      "Description",
      "Service Links",
      "Screenshots",
      "Additional Information",
      "Additional Documents",
      "Licensing",
      "Category",
      "Targeting",
      "Rollout Settings",
    ];

    if (hasPaymentRole) {
      sideMenu.push("Monetize");
    }

    return sideMenu;
  }

  const sideMenu = [
    "APK File",
    "Description",
    "Service Links",
    "Screenshots",
    "Additional Information",
    "Additional Documents",
    "Category",
    "Targeting",
    "Rollout Settings",
  ];

  if (hasPaymentRole) {
    sideMenu.push("Monetize");
  }

  if (
    submissionData.isMonetized &&
    (submissionData.isInAppPurchase || submissionData.isPremiumApp)
  ) {
    sideMenu.push("Monetize");
  }

  return sideMenu;
};

const buildOemSideMenu = ({
  portal,
  currentUrl,
  submissionData,
  idpLogin,
  thirdPartyApps,
}) => {
  if (
    currentUrl.includes("publisher-info") ||
    currentUrl.includes("activity-log")
  ) {
    return ["App Details"];
  }

  if (submissionData.isOemApp) {
    if (idpLogin && !thirdPartyApps) {
      return [
        "APK File",
        "Description",
        "Service Links",
        "Screenshots",
        "Additional Information",
        "Additional Documents",
        "Licensing",
        "Category",
        "Legal Documentation",
        "Targeting",
        "Rollout Settings",
      ];
    }

    return [
      "APK File",
      "Description",
      "Service Links",
      "Screenshots",
      "Additional Information",
      "Additional Documents",
      "Licensing",
      "Category",
      "Targeting",
      "Rollout Settings",
    ];
  }

  const sideMenu =
    portal === "oem-admin"
      ? [
          "APK File",
          "Description",
          "Service Links",
          "Screenshots",
          "Additional Information",
          "Additional Documents",
          "Category",
          "Targeting",
          "Rollout Settings",
        ]
      : [
          "APK File",
          "Description",
          "Screenshots",
          "Additional Information",
          "Additional Documents",
          "Cluster compatibility",
          "DLCM",
          "Category",
          "Targeting",
          "Rollout Settings",
        ];

  if (
    submissionData.isMonetized &&
    (submissionData.isInAppPurchase || submissionData.isPremiumApp)
  ) {
    sideMenu.push("Monetize");
  }

  return sideMenu;
};

const getSideMenuItems = ({
  portal,
  thirdPartyApps,
  currentUrl,
  submissionData,
  idpLogin,
  isOemSeller,
  hasPaymentRole,
}) => {
  if (
    portal === "seller" ||
    portal === "global-admin" ||
    (portal === "domain-approver" && !thirdPartyApps)
  ) {
    return buildStandardSideMenu({
      idpLogin,
      isOemSeller,
      hasPaymentRole,
      submissionData,
    });
  }

  if (
    portal === "oem-admin" ||
    (portal === "domain-approver" && thirdPartyApps)
  ) {
    return buildOemSideMenu({
      portal,
      currentUrl,
      submissionData,
      idpLogin,
      thirdPartyApps,
    });
  }

  return [];
};

const getRouteState = (currentUrl, customPortal) => {
  const routeState = {
    isRelease: currentUrl.includes("releases"),
    isActivityLog: currentUrl.includes("activity-log"),
    isTeamsInfo: currentUrl.includes("teamsInfo"),
    isPublisherInfo: currentUrl.includes("publisher-info"),
    isQAprocess: currentUrl.includes("qa-process"),
    isDashboard: false,
    isPaymentsPage: false,
    isInAppPage:
      !currentUrl.includes("premium-product") &&
      (currentUrl.includes("plan") || currentUrl.includes("product")),
    isPremiumPage: currentUrl.includes("premium-product"),
    isManageClient: currentUrl.includes("api-client"),
    activeMenuItem: null,
  };

  if (customPortal === "oem-admin") {
    routeState.isPaymentsPage =
      currentUrl.includes("product") || currentUrl.includes("plan");
  }

  const isStatsRoute =
    currentUrl.includes("dashboard") ||
    currentUrl.includes("crash-report") ||
    currentUrl.includes("oem-status");

  if (isStatsRoute) {
    routeState.isDashboard = true;

    if (currentUrl.includes("dashboard")) {
      routeState.activeMenuItem = "Dashboard";
    } else if (currentUrl.includes("crash-report")) {
      routeState.activeMenuItem = "Crash Reports";
    } else if (currentUrl.includes("oem-status")) {
      routeState.activeMenuItem = "OEM Activity Status";
    }
  }

  return routeState;
};

const shouldFetchSubmissions = (portal, thirdPartyApps, appData) =>
  portal === "seller" ||
  portal === "global-admin" ||
  (portal === "domain-approver" && !thirdPartyApps && hasAppData(appData));

const shouldEnablePaymentRole = (paymentRole) =>
  paymentRole.includes("IGNITESTOREMONETIZE_SCP") ||
  paymentRole.includes("IGNITESTOREMONETIZEPREMIUM_SCP");

const syncSidebarState = ({
  portal,
  thirdPartyApps,
  appData,
  dispatch,
  paymentRole,
  setHasPaymentRole,
  appMultiSubmissionId,
  submissionId,
  setVersionId,
  submissionData,
  idpLogin,
  isOemSeller,
  hasPaymentRole,
  setSideMenuData,
  customPortal,
  setIsQAprocess,
  setIsPublisherInfo,
  setIsRelease,
  setIsActivityLog,
  setIsTeamsInfo,
  setInfoData,
  domain,
  setisManageClient,
  setIsPaymentsPage,
  setIsDashboard,
  setActiveMenuItem,
  setIsInAppPage,
  setIsPremiumPage,
}) => {
  if (shouldFetchSubmissions(portal, thirdPartyApps, appData)) {
    dispatch(fetchAllSubmissions(appData.id));
  }

  if (shouldEnablePaymentRole(paymentRole)) {
    setHasPaymentRole(true);
  }

  if (
    hasAppData(appData) &&
    appMultiSubmissionId === undefined &&
    appMultiSubmissionId === ""
  ) {
    setVersionId(appData.submissions[0].id);
  }

  if (appMultiSubmissionId !== undefined && submissionId === "") {
    setVersionId(appMultiSubmissionId);
  } else if (submissionId && submissionId.length > 0) {
    setVersionId(submissionId);
  } else if (hasAppData(appData)) {
    setVersionId(appData.submissions[0].id);
  }

  const currentUrl = globalThis.location.href;

  setSideMenuData(
    getSideMenuItems({
      portal,
      thirdPartyApps,
      currentUrl,
      submissionData,
      idpLogin,
      isOemSeller,
      hasPaymentRole,
    })
  );

  const routeState = getRouteState(currentUrl, customPortal);

  setIsQAprocess(routeState.isQAprocess);
  setIsPublisherInfo(routeState.isPublisherInfo);
  setIsRelease(routeState.isRelease);
  setIsActivityLog(routeState.isActivityLog);
  setIsTeamsInfo(routeState.isTeamsInfo);
  setisManageClient(routeState.isManageClient);
  setIsPaymentsPage(routeState.isPaymentsPage);
  setIsDashboard(routeState.isDashboard);
  setIsInAppPage(routeState.isInAppPage);
  setIsPremiumPage(routeState.isPremiumPage);

  if (routeState.isManageClient) {
    setInfoData(getManageClientInfoData(portal, domain));
  }

  if (routeState.activeMenuItem) {
    setActiveMenuItem(routeState.activeMenuItem);
  }
};

const getTargetingScrollId = (portal) =>
  portal === "oem-admin" ? "oem-admin-targeting" : "targeting";

const getMonetizeScrollId = (portal) =>
  portal === "seller" ? "monetizeApplication" : "monetizePricing";

const navigateToAppDetails = (navigate) => {
  const portfolioId = sessionStorage.getItem("portfolioId");
  const contentId = sessionStorage.getItem("contentId");

  if (portfolioId && contentId) {
    const targetRoute = `/ui/oem-admin/applications/${portfolioId}/content/${contentId}`;

    if (globalThis.location.pathname === targetRoute) {
      globalThis.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate(targetRoute);
    }

    return;
  }

  const currentPath = globalThis.location.pathname;
  const pathParts = currentPath.split("/");
  const portfolioIndex = pathParts.indexOf("applications") + 1;
  const contentIndex = pathParts.indexOf("content") + 1;

  if (
    portfolioIndex > 0 &&
    contentIndex > 0 &&
    pathParts[portfolioIndex] &&
    pathParts[contentIndex]
  ) {
    const fallbackPortfolioId = pathParts[portfolioIndex];
    const fallbackContentId = pathParts[contentIndex];
    const targetRoute = `/ui/oem-admin/applications/${fallbackPortfolioId}/content/${fallbackContentId}`;

    if (globalThis.location.pathname === targetRoute) {
      globalThis.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate(targetRoute);
    }

    return;
  }

  console.error(
    "Could not navigate to App Details: missing portfolioId or contentId",
    {
      portfolioIndex,
      contentIndex,
      pathParts,
    }
  );

  const baseAppPath = currentPath.split("/activity-log")[0];
  if (baseAppPath !== currentPath) {
    navigate(baseAppPath);
  }
};

const clearCurrentLanguage = () => {
  sessionStorage.removeItem("currentLanguage");
};

const getReleasesPath = (portal, appId, submissionId) => {
  if (portal === "domain-approver") {
    return `/ui/domain-approver/applications/${appId}/submission/${submissionId}/releases`;
  }

  if (portal === "global-admin") {
    return `/ui/harman-admin/application/${appId}/submission/${submissionId}/releases`;
  }

  return `/ui/developer/applications/${appId}/submission/${submissionId}/releases`;
};

const getActivityLogPath = (portal, appId, submissionId) => {
  if (portal === "oem-admin") {
    return `/ui/oem-admin/applications/${sessionStorage.getItem(
      "portfolioId"
    )}/content/${sessionStorage.getItem("contentId")}/activity-log`;
  }

  if (portal === "domain-approver") {
    return `/ui/domain-approver/applications/${appId}/submission/${submissionId}/activity-log`;
  }

  if (portal === "global-admin") {
    return `/ui/harman-admin/application/${appId}/submission/${submissionId}/activity-log`;
  }

  return `/ui/developer/applications/${appId}/submission/${submissionId}/activity-log`;
};

const getPublisherInfoPath = (portal, appId, submissionId) => {
  if (portal === "oem-admin") {
    return `/ui/oem-admin/publisher-info/${sessionStorage.getItem(
      "portfolioId"
    )}/${sessionStorage.getItem("contentId")}`;
  }

  return `/ui/harman-admin/application/${appId}/submission/${submissionId}/publisher-info`;
};

const getSubmissionPath = (portal, appId, submissionId) => {
  if (portal === "global-admin") {
    return `/ui/harman-admin/application/${appId}/submission/${submissionId}`;
  }

  if (portal === "domain-approver") {
    return `/ui/domain-approver/applications/${appId}/submission/${submissionId}`;
  }

  return null;
};

const getInfoItemActiveState = (text, routeState) => {
  if (text === "Team Info") {
    return routeState.isTeamsInfo;
  }
  if (text === "Activity Log") {
    return routeState.isActivityLog;
  }
  if (text === "All Releases") {
    return routeState.isRelease;
  }
  if (text === "Manage API Client") {
    return routeState.isManageClient;
  }

  return false;
};

const getOemInfoItemActiveState = (text, routeState) => {
  if (text === "Publisher Info") {
    return routeState.isPublisherInfo;
  }
  if (text === "Activity Log") {
    return routeState.isActivityLog;
  }
  if (text === "All Releases") {
    return routeState.isRelease;
  }

  return false;
};

const getGlobalAdminInfoItemActiveState = (text, routeState) => {
  if (text === "Publisher Info") {
    return routeState.isPublisherInfo;
  }
  if (text === "Activity Log") {
    return routeState.isActivityLog;
  }
  if (text === "All Releases") {
    return routeState.isRelease;
  }
  if (text === "QA Process") {
    return routeState.isQAprocess;
  }

  return false;
};

const getMonetizeItemActiveState = (text, routeState) => {
  if (text === "In-App Products") {
    return routeState.isInAppPage;
  }
  if (text === "App Pricing") {
    return routeState.isPremiumPage;
  }

  return false;
};

const createMenuItemHandlers = ({
  setActiveMenuItem,
  scrollId,
  portal,
  navigate,
}) => ({
  "APK File": () => {
    setActiveMenuItem("APK File");
    scrollId(SECTION_SCROLL_TARGETS["APK File"]);
  },
  Description: () => {
    setActiveMenuItem("Description");
    scrollId(SECTION_SCROLL_TARGETS.Description);
  },
  "Service Links": () => {
    setActiveMenuItem("Service Links");
    scrollId(SECTION_SCROLL_TARGETS["Service Links"]);
  },
  Screenshots: () => {
    setActiveMenuItem("Screenshots");
    scrollId(SECTION_SCROLL_TARGETS.Screenshots);
  },
  "Additional Information": () => {
    setActiveMenuItem("Additional Information");
    scrollId(SECTION_SCROLL_TARGETS["Additional Information"]);
  },
  "Additional Documents": () => {
    setActiveMenuItem("Additional Documents");
    scrollId(SECTION_SCROLL_TARGETS["Additional Documents"]);
  },
  Category: () => {
    setActiveMenuItem("Category");
    scrollId(SECTION_SCROLL_TARGETS.Category);
  },
  Targeting: () => {
    setActiveMenuItem("Targeting");
    scrollId(getTargetingScrollId(portal));
  },
  "Rollout Settings": () => {
    setActiveMenuItem("Rollout Settings");
    scrollId(SECTION_SCROLL_TARGETS["Rollout Settings"]);
  },
  Licensing: () => {
    setActiveMenuItem("Licensing");
    scrollId(SECTION_SCROLL_TARGETS.Licensing);
  },
  "Legal Documentation": () => {
    setActiveMenuItem("Legal Documentation");
    scrollId(SECTION_SCROLL_TARGETS["Legal Documentation"]);
  },
  "Cluster compatibility": () => {
    setActiveMenuItem("Cluster compatibility");
    scrollId(SECTION_SCROLL_TARGETS["Cluster compatibility"]);
  },
  DLCM: () => {
    setActiveMenuItem("DLCM");
    scrollId(SECTION_SCROLL_TARGETS.DLCM);
  },
  Monetize: () => {
    setActiveMenuItem("Monetize");
    scrollId(getMonetizeScrollId(portal));
  },
  "App Details": () => {
    setActiveMenuItem("App Details");
    navigateToAppDetails(navigate);
  },
});

const PUBLISHED_STATUSES = new Set([
  "PUBLISHED",
  "APPROVED_FOR_PRODUCTION",
  "APPROVED_FOR_BETA",
  "STAGING_APPROVAL_APPROVED",
  "PRODUCTION_APPROVAL_APPROVED",
  "STAGING_APPROVAL_IN_REVIEW",
  "PRODUCTION_APPROVAL_IN_REVIEW",
]);

const findPublishedSubmissionId = (allSubmissionsData, fallbackId, appData) => {
  // First try allSubmissionsData (full submission details from API)
  if (Array.isArray(allSubmissionsData) && allSubmissionsData.length > 0) {
    const published = allSubmissionsData.find((s) =>
      PUBLISHED_STATUSES.has(s.status)
    );
    if (published?.id) return published.id;
  }
  // Fallback to appData.submissions (available immediately)
  if (appData?.submissions && appData.submissions.length > 0) {
    const published = appData.submissions.find((s) =>
      PUBLISHED_STATUSES.has(s.status)
    );
    if (published?.id) return published.id;
    // If no published found but multiple submissions exist, use the non-draft one
    if (appData.submissions.length > 1 && appData.submissions[0]?.status === "DRAFT") {
      return appData.submissions[1]?.id || fallbackId;
    }
  }
  return fallbackId;
};

const getValidStoredSubmissionId = (appData, allSubmissionsData) => {
  const stored = sessionStorage.getItem("oemStatusSubmissionId");
  if (!stored) return null;

  // Check if stored ID belongs to the current app's submissions
  const appSubmissionIds = new Set();
  if (appData?.submissions) {
    appData.submissions.forEach((s) => appSubmissionIds.add(s.id));
  }
  if (Array.isArray(allSubmissionsData)) {
    allSubmissionsData.forEach((s) => appSubmissionIds.add(s.id));
  }

  // Only use stored ID if it belongs to this app
  if (appSubmissionIds.size > 0 && !appSubmissionIds.has(stored)) {
    return null;
  }
  return stored;
};

const createPageNavigationHandlers = ({
  setActiveMenuItem,
  navigate,
  portal,
  appData,
  submissionId,
  teamId,
  setIsPublisherInfo,
  setIsQAprocess,
  appMultiSubmissionId,
  allSubmissionsData,
}) => ({
  "All Releases": () => {
    setActiveMenuItem("All Releases");
    const storedSubmissionId = getValidStoredSubmissionId(appData, allSubmissionsData) || findPublishedSubmissionId(allSubmissionsData, submissionId, appData);
    navigate(getReleasesPath(portal, appData?.id, storedSubmissionId));
    clearCurrentLanguage();
  },
  "Team Info": () => {
    setActiveMenuItem("Team Info");
    const storedSubmissionId = getValidStoredSubmissionId(appData, allSubmissionsData) || findPublishedSubmissionId(allSubmissionsData, submissionId, appData);
    navigate(`/ui/developer/applications/${appData?.id}/teamsInfo`, {
      state: { submissionId: storedSubmissionId },
    });
    clearCurrentLanguage();
  },
  "Activity Log": () => {
    setActiveMenuItem("Activity Log");
    const storedSubmissionId = getValidStoredSubmissionId(appData, allSubmissionsData) || findPublishedSubmissionId(allSubmissionsData, submissionId, appData);
    navigate(getActivityLogPath(portal, appData?.id, storedSubmissionId));
    clearCurrentLanguage();
  },
  Dashboard: () => {
    setActiveMenuItem("Dashboard");
    const dashboardSubmissionId = getValidStoredSubmissionId(appData, allSubmissionsData) || findPublishedSubmissionId(allSubmissionsData, submissionId, appData);

    if (
      appData?.id &&
      dashboardSubmissionId &&
      dashboardSubmissionId !== "" &&
      dashboardSubmissionId !== "true"
    ) {
      navigate(
        `/ui/developer/applications/${appData.id}/submission/${dashboardSubmissionId}/dashboard`
      );
    } else {
      console.error(
        "Missing appData.id or submissionId for dashboard navigation",
        {
          appDataId: appData?.id,
          submissionId: dashboardSubmissionId,
          submissionIdType: typeof dashboardSubmissionId,
          appMultiSubmissionId,
        }
      );
    }

    clearCurrentLanguage();
  },
  "Crash Reports": () => {
    setActiveMenuItem("Crash Reports");
    const crashReportSubmissionId = getValidStoredSubmissionId(appData, allSubmissionsData) || findPublishedSubmissionId(allSubmissionsData, submissionId, appData);
    navigate(
      `/ui/developer/applications/${appData?.id}/submission/${crashReportSubmissionId}/crash-report`
    );
    clearCurrentLanguage();
  },
  "OEM Activity Status": () => {
    setActiveMenuItem("OEM Activity Status");
    const oemSubmissionId = getValidStoredSubmissionId(appData, allSubmissionsData) || findPublishedSubmissionId(allSubmissionsData, submissionId, appData);
    navigate(
      `/ui/developer/applications/${appData?.id}/submission/${oemSubmissionId}/oem-status`
    );
    clearCurrentLanguage();
  },
  "In-App Products": () => {
    setActiveMenuItem("In-App Products");
    navigate(`/ui/developer/applications/${appData?.id}/app-product`, {
      state: { teamId },
    });
    clearCurrentLanguage();
  },
  "App Pricing": () => {
    setActiveMenuItem("App Pricing");
    navigate(`/ui/developer/applications/${appData?.id}/premium-product`, {
      state: { teamId },
    });
    clearCurrentLanguage();
  },
  "Publisher Info": () => {
    setActiveMenuItem("Publisher Info");
    navigate(getPublisherInfoPath(portal, appData?.id, submissionId));
    setIsPublisherInfo(true);
  },
  "QA Process": () => {
    setActiveMenuItem("QA Process");
    navigate(
      `/ui/harman-admin/application/${appData?.id}/submission/${submissionId}/qa-process`
    );
    setIsQAprocess(true);
  },
  "Manage API Client": () => {
    setActiveMenuItem("Manage API Client");
    navigate(`/ui/developer/api-client/applications/${appData?.id}`, {
      state: { teamId, submissionId },
    });
    clearCurrentLanguage();
  },
});

const getSidebarVisibilityFlags = ({
  portal,
  thirdPartyApps,
  isRelease,
  isActivityLog,
  isPublisherInfo,
  isQAprocess,
  isTeamsInfo,
  isDashboard,
  isManageClient,
  versionId,
  isPaymentsPage,
  sideMenuData,
  hasPaymentRole,
  isMonetized,
}) => {
  const isStatsRoute =
    globalThis.location.pathname.includes("dashboard") ||
    globalThis.location.pathname.includes("crash-report") ||
    globalThis.location.pathname.includes("oem-status");
  const isDomainApproverThirdParty =
    portal === "domain-approver" && thirdPartyApps;
  const notInSpecialView =
    !isRelease &&
    !isActivityLog &&
    !isPublisherInfo &&
    !isQAprocess &&
    !isTeamsInfo &&
    !isDashboard &&
    !isManageClient &&
    !isStatsRoute;
  const hasVersion = Boolean(versionId && versionId.length > 0);

  return {
    showReleaseVersionLabel:
      notInSpecialView && !isDomainApproverThirdParty && portal !== "oem-admin",
    showVersionSelector: hasVersion && notInSpecialView && portal !== "oem-admin",
    showOemVersionSelector:
      !hasVersion && notInSpecialView && portal === "oem-admin",
    showSideMenuList:
      !isRelease &&
      (!isActivityLog || portal === "oem-admin") &&
      (!isPublisherInfo || portal === "oem-admin") &&
      !isQAprocess &&
      !isTeamsInfo &&
      !isDashboard &&
      !isPaymentsPage &&
      !isManageClient &&
      !isStatsRoute &&
      sideMenuData &&
      sideMenuData.length > 0,
    showMonetizeSection:
      hasPaymentRole && isMonetized && !isDashboard && !isPublisherInfo && !isStatsRoute,
    showInfoSectionTitle:
      portal === "seller" ||
      portal === "global-admin" ||
      (portal === "domain-approver" && !thirdPartyApps) ||
      portal === "oem-admin",
    showSellerApproverInfoList:
      portal === "seller" || (portal === "domain-approver" && !thirdPartyApps),
    showStatisticsSection: !isActivityLog && !isPaymentsPage && portal === "seller",
    showStatisticsList: !isPaymentsPage && portal === "seller",
  };
};

const getSidebarDrawerStyles = ({
  isTeamsInfo,
  isActivityLog,
  isRelease,
  isQAprocess,
  isManageClient,
  isPublisherInfo,
  isDashboard,
  portal,
  isScrolling,
  idpLogin,
  submissionData,
}) => {
  const notInNav =
    !isTeamsInfo &&
    !isActivityLog &&
    !isRelease &&
    !isQAprocess &&
    !isManageClient &&
    !isPublisherInfo;
  const notInOemNav =
    !isActivityLog &&
    !isTeamsInfo &&
    !isRelease &&
    !isDashboard &&
    !isQAprocess &&
    !isPublisherInfo &&
    !isManageClient;

  return {
    shouldUseAbsolutePosition:
      isScrolling &&
      (portal !== "oem-admin" ||
        (portal === "oem-admin" && idpLogin && submissionData.isOemApp)),
    drawerWidthForViewport: notInNav ? "225px !important" : "180px !important",
    compactListHeight: notInOemNav && portal !== "oem-admin" ? "440px !important" : "390px !important",
    compactListOverflowY: notInOemNav ? "auto" : "hidden",
  };
};

const getSidebarViewState = ({
  portal,
  thirdPartyApps,
  isRelease,
  isActivityLog,
  isPublisherInfo,
  isQAprocess,
  isTeamsInfo,
  isDashboard,
  isManageClient,
  versionId,
  isPaymentsPage,
  sideMenuData,
  hasPaymentRole,
  isMonetized,
  isScrolling,
  idpLogin,
  submissionData,
  domain,
  infoData,
}) => ({
  ...getSidebarVisibilityFlags({
    portal,
    thirdPartyApps,
    isRelease,
    isActivityLog,
    isPublisherInfo,
    isQAprocess,
    isTeamsInfo,
    isDashboard,
    isManageClient,
    versionId,
    isPaymentsPage,
    sideMenuData,
    hasPaymentRole,
    isMonetized,
  }),
  ...getSidebarDrawerStyles({
    isTeamsInfo,
    isActivityLog,
    isRelease,
    isQAprocess,
    isManageClient,
    isPublisherInfo,
    isDashboard,
    portal,
    isScrolling,
    idpLogin,
    submissionData,
  }),
  filteredInfoData: infoData.filter(
    (text) =>
      !(
        portal === "domain-approver" &&
        domain === "VWGroup" &&
        text === "Manage API Client"
      )
  ),
});

const STATISTICS_ITEMS = ["Dashboard", "Crash Reports", "OEM Activity Status"];

const OEM_STATUS_VISIBLE_STATUSES = new Set([
  "APPROVED_FOR_PRODUCTION",
  "PUBLISHED",
]);

const StatisticsSection = ({
  showStatisticsSection,
  showStatisticsList,
  activeMenuItem,
  pageNavigationHandlers,
  submissionStatus,
}) => {
  const visibleItems = STATISTICS_ITEMS.filter(
    (item) =>
      item !== "OEM Activity Status" ||
      OEM_STATUS_VISIBLE_STATUSES.has(submissionStatus),
  );

  return (
  <>
    {showStatisticsSection && (
      <p className="info-side-menu-text">STATISTICS</p>
    )}
    {showStatisticsList && (
      <List sx={{ marginTop: "-10px !important" }}>
        {visibleItems.map((text) => (
          <ListItem
            key={text}
            disablePadding
            sx={{
              paddingTop: "0px !important",
              paddingBottom: "0px !important",
              marginTop: "-2px !important",
              width: "95%",
            }}
          >
            <ListItemButton
              sx={{
                "&:hover": {
                  backgroundColor: "#F3F3F3 !important",
                  color: "#0476E2 !important",
                  borderRadius: "4px",
                },
                paddingTop: "0px !important",
                paddingBottom: "0px !important",
                fontSize: "11px !important",
                "&.MuiListItemButton-root": {
                  marginLeft: "14px !important",
                  paddingLeft: "0px !important",
                  paddingRight: "0px !important",
                  borderRadius: "4px",
                },
              }}
              onClick={() => pageNavigationHandlers[text]?.()}
              className={activeMenuItem === text ? "activeMenuItem" : ""}
            >
              <ListItemText
                sx={{
                  "&:hover": {},
                  fontSize: "11px !important",
                  fontWeight: activeMenuItem === text ? "bold" : "400",
                  lineHeight: "20px",
                  letterSpacing: "0.25px",
                  textAlign: "left",
                  color: activeMenuItem === text ? "#0476E2" : "#2A2C2D",
                  paddingLeft: "20px",
                }}
                primary={text}
                disableTypography
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    )}
  </>
  );
};

const VersionSection = ({
  showReleaseVersionLabel,
  showVersionSelector,
  showOemVersionSelector,
  versionId,
  allSubmissionsData,
  handleChange,
  oemVersionId,
}) => (
  <>
    {showReleaseVersionLabel && (
      <p className="info-side-menu-text" style={{ marginTop: "3px !important" }}>
        RELEASE VERSION
      </p>
    )}
    {showVersionSelector ? (
      <div className="version-side-menu-select">
        <Select
          className="input-select-activity-log-select"
          sx={{
            border: "none",
            "& .MuiList-root": {
              padding: "0px",
            },
            "& .MuiMenuItem-root": {
              borderRadius: "4px",
              "&:hover": {
                backgroundColor: "#E5F3FF",
                color: "#E5F3FF",
                padding: "8px",
              },
            },
          }}
          defaultValue={versionId}
          value={versionId}
          onChange={handleChange}
          variant="outlined"
        >
          {allSubmissionsData &&
            allSubmissionsData.length > 0 &&
            allSubmissionsData
              .slice()
              .sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt))
              .map((item) => (
                <MenuItem
                  sx={{
                    padding: "8px !important",
                    fontSize: "12px",
                    "&.Mui-selected": {
                      backgroundColor: "#E5F3FF",
                      borderRadius: "4px",
                      color: "#0476E2",
                      fontWeight: "600",
                    },
                    "&.MuiMenuItem-root": {
                      marginLeft: "8px !important",
                      marginRight: "8px !important",
                    },
                    "&:hover": {
                      borderRadius: "4px",
                    },
                  }}
                  key={item.id}
                  value={item.id}
                >
                  {item.versionName}
                </MenuItem>
              ))}
        </Select>
      </div>
    ) : (
      showOemVersionSelector && (
        <>
          <p
            className="info-side-menu-text"
            style={{
              width: "234px",
              height: "14px",
              fontSize: "12px",
              fontWeight: "600",
              lineHeight: "120%",
              letterSpacing: "4%",
              verticalAlign: "middle",
              color: "#717273",
            }}
          >
            RELEASE VERSION
          </p>
          <div className="version-side-menu-select">
            <Select
              className="input-select-activity-log-select"
              sx={{
                border: "none",
                "& .MuiList-root": {
                  padding: "0px",
                },
                "& .MuiMenuItem-root": {
                  borderRadius: "4px",
                  "&:hover": {
                    backgroundColor: "#E5F3FF",
                    color: "#E5F3FF",
                    padding: "8px",
                  },
                },
              }}
              defaultValue={oemVersionId}
              value={oemVersionId}
              onChange={handleChange}
              variant="outlined"
            >
              <MenuItem
                sx={{
                  padding: "8px !important",
                  fontSize: "12px",
                  "&.Mui-selected": {
                    backgroundColor: "#E5F3FF",
                    borderRadius: "4px",
                    color: "#0476E2",
                    fontWeight: "600",
                  },
                  "&.MuiMenuItem-root": {
                    marginLeft: "8px !important",
                    marginRight: "8px !important",
                  },
                  "&:hover": {
                    borderRadius: "4px",
                  },
                }}
                key={oemVersionId}
                value={oemVersionId}
              >
                {oemVersionId}
              </MenuItem>
            </Select>
          </div>
        </>
      )
    )}
  </>
);

const SideMenuSection = ({
  showSideMenuList,
  sideMenuData,
  activeMenuItem,
  menuItemHandlers,
}) =>
  showSideMenuList ? (
    <List>
      {sideMenuData.map((text) => (
        <ListItem key={text} disablePadding sx={{ width: "96%" }}>
          <ListItemButton
            sx={{
              "&:hover": {
                backgroundColor: "#F3F3F3 !important",
                color: "#0476E2 !important",
                borderRadius: "4px",
              },
              paddingTop: "0px",
              paddingBottom: "0px",
              "&.MuiListItemButton-root": {
                marginLeft: "19px !important",
                paddingLeft: "0px !important",
                paddingRight: "0px !important",
                borderRadius: "4px",
              },
            }}
            onClick={() => menuItemHandlers[text]?.()}
            className={activeMenuItem === text ? "activeMenuItem" : ""}
          >
            <ListItemText
              sx={{
                "&:hover": {},
                fontSize: "11px",
                fontWeight: activeMenuItem === text ? "bold" : "400",
                lineHeight: "20px",
                letterSpacing: "0.25px",
                textAlign: "left",
                color: activeMenuItem === text ? "#0476E2" : "#2A2C2D",
                paddingLeft: "15px",
              }}
              primary={text}
              disableTypography
            />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  ) : null;

const MonetizeSection = ({
  showMonetizeSection,
  pageNavigationHandlers,
  isInAppPage,
  isPremiumPage,
}) =>
  showMonetizeSection ? (
    <>
      <p className="info-side-menu-text">MONETIZE</p>
      <List>
        {["App Pricing", "In-App Products"].map((text) => {
          const isActive = getMonetizeItemActiveState(text, {
            isInAppPage,
            isPremiumPage,
          });

          return (
            <ListItem key={text} disablePadding>
              <ListItemButton
                sx={{
                  backgroundColor: isActive ? "#E5F3FF" : "transparent",
                  "&:hover": {
                    backgroundColor: "#F3F3F3 !important",
                    color: "#0476E2 !important",
                    borderRadius: "4px",
                  },
                  "&.MuiListItemButton-root": {
                    marginLeft: "19px !important",
                    paddingLeft: "0px !important",
                  },
                  paddingTop: "0px",
                  paddingBottom: "0px",
                  paddingRight: "0px !important",
                }}
                onClick={() => pageNavigationHandlers[text]?.()}
              >
                <ListItemText
                  sx={{
                    "&:hover": {},
                    fontSize: "11px",
                    fontWeight: isActive ? "600" : "400",
                    lineHeight: "20px",
                    letterSpacing: "0.25px",
                    textAlign: "left",
                    color: isActive ? "#0476E2" : "#2A2C2D",
                    paddingLeft: "15px",
                  }}
                  primary={text}
                  disableTypography
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </>
  ) : null;

const InfoSection = ({
  showInfoSectionTitle,
  showSellerApproverInfoList,
  filteredInfoData,
  isTeamsInfo,
  isActivityLog,
  isRelease,
  isManageClient,
  portal,
  pageNavigationHandlers,
  isPublisherInfo,
  isQAprocess,
}) => (
  <>
    {showInfoSectionTitle && <p className="info-side-menu-text">INFO</p>}
    {showSellerApproverInfoList ? (
      <List>
        {filteredInfoData.map((text) => {
          const isActive = getInfoItemActiveState(text, {
            isTeamsInfo,
            isActivityLog,
            isRelease,
            isManageClient,
          });

          return (
            <ListItem key={text} disablePadding>
              <ListItemButton
                sx={{
                  backgroundColor: isActive ? "#E5F3FF !important" : "transparent",
                  "&:hover": {
                    backgroundColor: "#F3F3F3 !important",
                    color: "#0476E2 !important",
                    borderRadius: "4px",
                  },
                  paddingTop: "0px",
                  paddingBottom: "0px",
                  "&.MuiListItemButton-root": {
                    marginLeft: "19px !important",
                    paddingLeft: "0px !important",
                    paddingRight: "0px !important",
                    borderRadius: "4px",
                  },
                }}
                onClick={() => pageNavigationHandlers[text]?.()}
              >
                <ListItemText
                  primary={text}
                  disableTypography
                  sx={{
                    paddingLeft: "20px",
                    fontSize: "11px",
                    fontWeight: isActive ? "600" : "400",
                    lineHeight: "20px",
                    letterSpacing: "0.25px",
                    textAlign: "left",
                    color: isActive ? "#0476E2" : "#2A2C2D",
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    ) : (
      portal === "oem-admin" && (
        <List>
          {["Publisher Info", "Activity Log"].map((text) => {
            const isActive = getOemInfoItemActiveState(text, {
              isPublisherInfo,
              isActivityLog,
              isRelease,
            });

            return (
              <ListItem key={text} disablePadding>
                <ListItemButton
                  sx={{
                    backgroundColor: isActive ? "#E5F3FF !important" : "transparent",
                    "&:hover": {
                      backgroundColor: "#F3F3F3 !important",
                      color: "#0476E2 !important",
                      borderRadius: "4px",
                    },
                    paddingTop: "0px",
                    paddingBottom: "0px",
                    "&.MuiListItemButton-root": {
                      marginLeft: "19px !important",
                      paddingLeft: "0px !important",
                      paddingRight: "0px !important",
                      borderRadius: "4px",
                    },
                  }}
                  onClick={() => pageNavigationHandlers[text]?.()}
                >
                  <ListItemText
                    primary={text}
                    disableTypography
                    sx={{
                      paddingLeft: "15px",
                      fontSize: "11px",
                      fontWeight: isActive ? "600" : "400",
                      lineHeight: "20px",
                      letterSpacing: "0.25px",
                      textAlign: "left",
                      color: isActive ? "#0476E2" : "#2A2C2D",
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      )
    )}
    {portal === "global-admin" && (
      <div>
        <List>
          {["Publisher Info", "Activity Log", "All Releases", "QA Process"].map(
            (text) => {
              const isActive = getGlobalAdminInfoItemActiveState(text, {
                isPublisherInfo,
                isActivityLog,
                isRelease,
                isQAprocess,
              });

              return (
                <ListItem key={text} disablePadding>
                  <ListItemButton
                    sx={{
                      backgroundColor: isActive ? "#E5F3FF !important" : "transparent",
                      "&:hover": {
                        backgroundColor: "#F3F3F3 !important",
                        color: "#0476E2 !important",
                        borderRadius: "4px",
                      },
                      paddingTop: "0px",
                      paddingBottom: "0px",
                      "&.MuiListItemButton-root": {
                        marginLeft: "19px !important",
                        paddingLeft: "0px !important",
                        paddingRight: "0px !important",
                        borderRadius: "4px",
                      },
                    }}
                    onClick={() => pageNavigationHandlers[text]?.()}
                  >
                    <ListItemText
                      primary={text}
                      disableTypography
                      sx={{
                        paddingLeft: "15px",
                        fontSize: "11px",
                        fontWeight: isActive ? "600" : "400",
                        lineHeight: "20px",
                        letterSpacing: "0.25px",
                        textAlign: "left",
                        color: isActive ? "#0476E2" : "#2A2C2D",
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            }
          )}
        </List>
      </div>
    )}
  </>
);

const StyledDrawer = styled(Box)(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  [`& .MuiDrawer-paper`]: {
    width: drawerWidth,
    boxSizing: "border-box",
  },
}));

function ApplicationDetailsSideMenu({
  appData,
  callNewSubmissionId,
  appTeamId,
  isMonetized,
  appMultiSubmissionId,
  submissionDetails,
}) {
  const portal = getConfig().REACT_APP_PORTAL;
  const idpLogin = sessionStorage.getItem("idpLogin") === "true";
  const domain = sessionStorage.getItem("domain");
  const customPortal = sessionStorage.getItem("customPortal");
  const isOemSeller = Boolean(sessionStorage.getItem("isOemSeller"));
  const paymentRole = sessionStorage.getItem("payment_role") || "";
  const thirdPartyApps = globalThis.location.href.includes("third-party-apps");
  const teamId = appTeamId;
  const initialSubmissionValue = getInitialSubmissionValue(
    appMultiSubmissionId,
    submissionDetails,
    appData
  );

  const [versionId, setVersionId] = useState(initialSubmissionValue);
  const [submissionId, setSubmissionId] = useState(initialSubmissionValue);

  const drawerRef = useRef(null);
  const [activeMenuItem, setActiveMenuItem] = useState(null);
  const [isRelease, setIsRelease] = useState(false);
  const [isActivityLog, setIsActivityLog] = useState(false);
  const [isTeamsInfo, setIsTeamsInfo] = useState(false);
  const [isPublisherInfo, setIsPublisherInfo] = useState(false);
  const [isQAprocess, setIsQAprocess] = useState(false);
  const [isDashboard, setIsDashboard] = useState(false);
  const [isPaymentsPage, setIsPaymentsPage] = useState(false);
  const [isInAppPage, setIsInAppPage] = useState(false);
  const [isPremiumPage, setIsPremiumPage] = useState(false);
  const [isManageClient, setisManageClient] = useState(false);
  const [sideMenuData, setSideMenuData] = useState([]);
  const isScrolling = false;
  const [infoData, setInfoData] = useState(() =>
    getDefaultInfoData(portal, idpLogin, domain)
  );

  const navigate = useNavigate();
  const handleChange = ($event) => {
    const newSubmissionId = $event.target.value;
    const submissionPath = getSubmissionPath(portal, appData.id, newSubmissionId);

    setVersionId($event.target.value);
    setSubmissionId($event.target.value);
    sessionStorage.setItem("submissionId", $event.target.value);

    if (submissionPath) {
      navigate(submissionPath);
    }

    callNewSubmissionId($event.target.value);
    clearCurrentLanguage();
  };
  const dispatch = useDispatch();

  const allSubmissionsData = useSelector(
    (state) => state.applicationAllSubmissions.submissions
  );

  const submissionData = useSelector(
    (state) => state.submissionDetails.submissionDetails
  );
  const oemVersionId = submissionData?.contentVersion || "";

  const [hasPaymentRole, setHasPaymentRole] = useState(false);

  useEffect(() => {
    syncSidebarState({
      portal,
      thirdPartyApps,
      appData,
      dispatch,
      paymentRole,
      setHasPaymentRole,
      appMultiSubmissionId,
      submissionId,
      setVersionId,
      submissionData,
      idpLogin,
      isOemSeller,
      hasPaymentRole,
      setSideMenuData,
      customPortal,
      setIsQAprocess,
      setIsPublisherInfo,
      setIsRelease,
      setIsActivityLog,
      setIsTeamsInfo,
      setInfoData,
      domain,
      setisManageClient,
      setIsPaymentsPage,
      setIsDashboard,
      setActiveMenuItem,
      setIsInAppPage,
      setIsPremiumPage,
    });
  }, [
    appData,
    appMultiSubmissionId,
    customPortal,
    dispatch,
    domain,
    hasPaymentRole,
    idpLogin,
    isOemSeller,
    paymentRole,
    portal,
    submissionData,
    submissionId,
    thirdPartyApps,
  ]);

  useEffect(() => {
    if (appMultiSubmissionId !== undefined && appMultiSubmissionId !== "") {
      setVersionId(appMultiSubmissionId);
      setSubmissionId(appMultiSubmissionId);
    }
  }, [appMultiSubmissionId]);

  const scrollId = (id) => {
    setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) {
        return;
      }
      const yOffset = -290;
      const y = el.getBoundingClientRect().top + globalThis.scrollY + yOffset;
      globalThis.scrollTo({ top: y, behavior: "smooth" });
    }, 100);
  };

  const menuItemHandlers = createMenuItemHandlers({
    setActiveMenuItem,
    scrollId,
    portal,
    navigate,
  });

  const pageNavigationHandlers = createPageNavigationHandlers({
    setActiveMenuItem,
    navigate,
    portal,
    appData,
    submissionId,
    teamId,
    setIsPublisherInfo,
    setIsQAprocess,
    appMultiSubmissionId,
    allSubmissionsData,
  });

  const {
    showReleaseVersionLabel,
    showVersionSelector,
    showOemVersionSelector,
    showSideMenuList,
    showMonetizeSection,
    showInfoSectionTitle,
    showSellerApproverInfoList,
    showStatisticsSection,
    showStatisticsList,
    shouldUseAbsolutePosition,
    drawerWidthForViewport,
    compactListHeight,
    compactListOverflowY,
    filteredInfoData,
  } = getSidebarViewState({
    portal,
    thirdPartyApps,
    isRelease,
    isActivityLog,
    isPublisherInfo,
    isQAprocess,
    isTeamsInfo,
    isDashboard,
    isManageClient,
    versionId,
    isPaymentsPage,
    sideMenuData,
    hasPaymentRole,
    isMonetized,
    isScrolling,
    idpLogin,
    submissionData,
    domain,
    infoData,
  });

  return (
    <div>
      <StyledDrawer
        ref={drawerRef}
        sx={{
          position: shouldUseAbsolutePosition ? "absolute" : "fixed",
          top: isScrolling ? `${globalThis.innerHeight / 2}px` : "100px",
          zIndex: 1000,
          "@media(max-height:1080px)": {
            width: drawerWidthForViewport,
          },
        }}
      >
        <List
          sx={{
            "@media(max-height:745px)": {
              height: compactListHeight,
              overflowY: compactListOverflowY,
              overflowX: "hidden",
            },
          }}
        >
          <StatisticsSection
            showStatisticsSection={showStatisticsSection}
            showStatisticsList={showStatisticsList}
            activeMenuItem={activeMenuItem}
            pageNavigationHandlers={pageNavigationHandlers}
            submissionStatus={submissionData?.status}
          />
          <VersionSection
            showReleaseVersionLabel={showReleaseVersionLabel}
            showVersionSelector={showVersionSelector}
            showOemVersionSelector={showOemVersionSelector}
            versionId={versionId}
            allSubmissionsData={allSubmissionsData}
            handleChange={handleChange}
            oemVersionId={oemVersionId}
          />
          <SideMenuSection
            showSideMenuList={showSideMenuList}
            sideMenuData={sideMenuData}
            activeMenuItem={activeMenuItem}
            menuItemHandlers={menuItemHandlers}
          />
          <MonetizeSection
            showMonetizeSection={showMonetizeSection}
            pageNavigationHandlers={pageNavigationHandlers}
            isInAppPage={isInAppPage}
            isPremiumPage={isPremiumPage}
          />
          <InfoSection
            showInfoSectionTitle={showInfoSectionTitle}
            showSellerApproverInfoList={showSellerApproverInfoList}
            filteredInfoData={filteredInfoData}
            isTeamsInfo={isTeamsInfo}
            isActivityLog={isActivityLog}
            isRelease={isRelease}
            isManageClient={isManageClient}
            portal={portal}
            pageNavigationHandlers={pageNavigationHandlers}
            isPublisherInfo={isPublisherInfo}
            isQAprocess={isQAprocess}
          />
        </List>
      </StyledDrawer>
    </div>
  );
}

StatisticsSection.propTypes = {
  showStatisticsSection: PropTypes.bool.isRequired,
  showStatisticsList: PropTypes.bool.isRequired,
  activeMenuItem: PropTypes.string,
  pageNavigationHandlers: PropTypes.objectOf(PropTypes.func).isRequired,
  submissionStatus: PropTypes.string,
};

VersionSection.propTypes = {
  showReleaseVersionLabel: PropTypes.bool.isRequired,
  showVersionSelector: PropTypes.bool.isRequired,
  showOemVersionSelector: PropTypes.bool.isRequired,
  versionId: PropTypes.string.isRequired,
  allSubmissionsData: PropTypes.arrayOf(PropTypes.object),
  handleChange: PropTypes.func.isRequired,
  oemVersionId: PropTypes.string.isRequired,
};

VersionSection.defaultProps = {
  allSubmissionsData: [],
};

SideMenuSection.propTypes = {
  showSideMenuList: PropTypes.bool.isRequired,
  sideMenuData: PropTypes.arrayOf(PropTypes.string).isRequired,
  activeMenuItem: PropTypes.string,
  menuItemHandlers: PropTypes.objectOf(PropTypes.func).isRequired,
};

MonetizeSection.propTypes = {
  showMonetizeSection: PropTypes.bool.isRequired,
  pageNavigationHandlers: PropTypes.objectOf(PropTypes.func).isRequired,
  isInAppPage: PropTypes.bool.isRequired,
  isPremiumPage: PropTypes.bool.isRequired,
};

InfoSection.propTypes = {
  showInfoSectionTitle: PropTypes.bool.isRequired,
  showSellerApproverInfoList: PropTypes.bool.isRequired,
  filteredInfoData: PropTypes.arrayOf(PropTypes.string).isRequired,
  isTeamsInfo: PropTypes.bool.isRequired,
  isActivityLog: PropTypes.bool.isRequired,
  isRelease: PropTypes.bool.isRequired,
  isManageClient: PropTypes.bool.isRequired,
  portal: PropTypes.string.isRequired,
  pageNavigationHandlers: PropTypes.objectOf(PropTypes.func).isRequired,
  isPublisherInfo: PropTypes.bool.isRequired,
  isQAprocess: PropTypes.bool.isRequired,
};

ApplicationDetailsSideMenu.propTypes = {
  appData: PropTypes.object.isRequired,
  callNewSubmissionId: PropTypes.func.isRequired,
  appTeamId: PropTypes.string.isRequired,
  isMonetized: PropTypes.bool.isRequired,
  appMultiSubmissionId: PropTypes.string,
  submissionDetails: PropTypes.object.isRequired,
};

export default ApplicationDetailsSideMenu;
